import { useEffect, useState } from 'react';
import { message, Table, Button, Card, Space, Typography, Divider, Popconfirm } from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import "./template.scss";
import { tablePageSize } from '../../../utils/constants';
import { usePageTitle } from '../../../context/pageTitleContext';
import { useTranslation } from 'react-i18next';
import TemplateServices from '../../../services/templateServices';
import NewTemplate from './newTemplate';
import { useDispatch } from 'react-redux';
import { useUserAuth } from '../../../context/UserContext';
import { fetchStandardOrderTemplateFields, fetchTemplates } from '../../../state/slices/utilsSlice';
import ViewTemplate from './viewTemplate';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { adminGroup, Can, permissions } from '../../../utils/permissionUtils';
import { Template } from './templates.types';
dayjs.extend(utc);
dayjs.extend(timezone);
const { Link } = Typography;

const TemplateList = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useUserAuth();
    const { setPageTitle } = usePageTitle();
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loadingData, setLoadingData] = useState(false);
    const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
    const [openUpdateDrawer, setOpenUpdateDrawer] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

    useEffect(() => {
        if (!user) return;
        dispatch(fetchStandardOrderTemplateFields() as any);
        getTemplates();
    }, [dispatch, user]);

    useEffect(() => {
        setPageTitle(t('page.templates'));
    }, []);

    const showDrawer = (drawerName: string, record: Template | null) => {
        if (drawerName === "create") setOpenCreateDrawer(true);
        if (drawerName === "update") setOpenUpdateDrawer(true); setSelectedTemplate(record);
    };

    const onCloseDrawer = (drawerName: string) => {
        if (drawerName === "create") setOpenCreateDrawer(false);
        if (drawerName === "update") setOpenUpdateDrawer(false); setSelectedTemplate(null);
    };
    const getTemplates = async () => {
        const userCountryID = user.lastSelectedCountryID ?? null;
        setLoadingData(true);
        try {
            const respData = await TemplateServices.getTemplates(userCountryID as string);
            if (respData.success) {
                const res = respData.responseObject.value.templates;
                setTemplates(res);
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingData(false);
        }
    };

    const onRemoveTemplate = async (templateId: string) => {
        try {
            const respData = await TemplateServices.deleteTemplate(templateId);
            if (respData.success) {
                message.success(t('messages.success'));
                getTemplates();
                dispatch(fetchTemplates(user) as any);
            }
            else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
    };

    const columns = [
        {
            title: t('template.templateName'),
            dataIndex: 'templateName',
            key: 'templateName',
        },
        {
            title: t('order.salesOrganization'),
            dataIndex: 'salesOrganizationCode',
            key: 'salesOrganizationCode',
        },
        {
            title: t('order.shipTo'),
            dataIndex: 'shipToPartyCode',
            key: 'shipToPartyCode',
        },
        {
            title: t('order.soldTo'),
            dataIndex: 'soldToPartyCode',
            key: 'soldToPartyCode',
        },
        {
            title: t('order.orderType'),
            dataIndex: 'orderTypeCode',
            key: 'orderTypeCode',
        },
        {
            title: t('template.createdOn'),
            dataIndex: 'createdOn',
            key: 'createdOn',
            render: (text: string) => {
                return (text ? dayjs.utc(text).tz(dayjs.tz.guess()).format('DD/MM/YYYY') : "")
            }
        },
        {
            title: t('template.createdBy'),
            dataIndex: 'createdByName',
            key: 'createdByName',
        },
        {
            title: t('template.rpaTemplateType'),
            dataIndex: 'rpaTemplateTypeDescription',
            key: 'rpaTemplateTypeDescription'
        },
        {
            title: '',
            key: 'action',
            width: "90px",
            render: (text: string, record: Template) => {
                return (
                    <Space size="middle" direction='horizontal' style={{ columnGap: 5 }}>
                        <Link onClick={() => showDrawer("update", record)}><EyeOutlined className='editIcon' /></Link >
                        <Can permission={permissions.DELETE_TEMPLATE}>
                            <>
                                <Divider type="vertical" style={{ marginInline: 10 }} />
                                <Popconfirm
                                    placement="topRight"
                                    key="remove"
                                    title={t('messages.confirmationApprove')}
                                    icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                    okText={t('general.yes')}
                                    cancelText={t('general.no')}
                                    onConfirm={() => onRemoveTemplate(record.id)}
                                >
                                    <DeleteOutlined style={{ color: '#023B72' }} />
                                </Popconfirm>
                            </>
                        </Can>
                    </Space>
                )
            }
        }

    ];

    return (
        <div className='templates'>
            <NewTemplate open={openCreateDrawer} onClose={() => onCloseDrawer("create")} getTemplates={() => getTemplates()} />

            {selectedTemplate && (
                <ViewTemplate open={openUpdateDrawer} onClose={() => onCloseDrawer("update")} record={selectedTemplate} />
            )}

            <Divider />

            <Card size='small' extra={
                <Can permission={permissions.UPLOAD_TEMPLATE}>
                    <Button size='small' type="default" onClick={() => showDrawer("create", null)} icon={<PlusOutlined />}>{t('template.addNewTemplate')}</Button>
                </Can>
            }>
                <Table
                    dataSource={templates}
                    columns={columns}
                    rowKey={obj => obj.id}
                    pagination={{
                        pageSize: tablePageSize
                    }}
                    loading={loadingData}
                    size='small'
                    bordered
                />
            </Card>
        </div>
    )
}

export default TemplateList;